import { LAPTOP_MEDIA_QUERY } from '../../../common/components/utils/constants';
import selectors from './ClinicSelectors';
import clinicSort from './ClinicSort';
import clinicFilter from './ClinicFilter';
import clinicMap from './ClinicMap';

// Helper functions

/**
 *
 * Saves items state in `initState`
 * `initState` is dictionary with key/value pairs.
 *
 * @param {Object} initState
 * @param {array} items
 * @param {('name'|'value')} propKey
 * @param {string} propValue
 *
 * @return {Object}
 */
function updateInitStateForm({ initState, items, propKey, propValue = 'checked' }) {
	let state = { ...initState };

	items.forEach((item) => {
		state = { ...state, [item[propKey]]: item[propValue] };
	});

	return state;
}

export class ClinicSearch {
	/**
	 * @param {boolean} isWidget in widget we don't have all required elements which doesn't affect the functionality
	 */
	constructor(isWidget = false) {
		const requiredElementsDesktop = [
			selectors.ELEMENTS.mapContainerEle,
			selectors.ELEMENTS.listContainerEle,
			selectors.ELEMENTS.showMapTrigger,
			selectors.ELEMENTS.showListTrigger,
		];

		const requiredElementsMobile = [
			...(isWidget ? [] : [selectors.ELEMENTS.navigation]),
			selectors.ELEMENTS.mobileFilters,
			selectors.ELEMENTS.mobileSort,
			selectors.ELEMENTS.formFilters,
			selectors.ELEMENTS.formRadioList,
			selectors.ELEMENTS.sortFormRadioList,
			selectors.ELEMENTS.formCheckboxList,
			selectors.ELEMENTS.hideFiltersContainer,
			selectors.ELEMENTS.hideSortContainer,
			selectors.ELEMENTS.hideFilters,
			selectors.ELEMENTS.showFilters,
			selectors.ELEMENTS.clearFilters,
			selectors.ELEMENTS.saveFilters,
			selectors.ELEMENTS.hideSort,
			selectors.ELEMENTS.showSort,
			selectors.ELEMENTS.clearSort,
			selectors.ELEMENTS.saveSort,
			selectors.ELEMENTS.infoFilters,
			selectors.ELEMENTS.labelFilters,
			selectors.ELEMENTS.labelSort,
		];

		if ([...requiredElementsMobile, ...requiredElementsDesktop].some((ele) => !ele)) {
			throw new Error('ClinicSearch not initialized');
		}

		/**
		 * If a user closes the dropdown without any action(clear, select),
		 * component will use `initState` to return to its initial state.
		 *
		 * @type {{items: {}}}
		 */
		this.initState = {};

		this.initState = updateInitStateForm({
			initState: this.initState,
			items: selectors.ELEMENTS.formRadioList,
			propKey: 'value',
			propValue: 'checked',
		});

		this.initState = updateInitStateForm({
			initState: this.initState,
			items: selectors.ELEMENTS.formCheckboxList,
			propKey: 'name',
			propValue: 'checked',
		});

		/** SORT
		 * If a user closes the dropdown without any action(clear, select),
		 * component will use `initStateSort` to return to its initial state.
		 *
		 * @type {{items: {}}}
		 */
		this.initStateSort = {};

		this.initStateSort = updateInitStateForm({
			initState: this.initStateSort,
			items: selectors.ELEMENTS.sortFormRadioList,
			propKey: 'value',
			propValue: 'checked',
		});

		this.mediaQuery = window.matchMedia(LAPTOP_MEDIA_QUERY);
		selectors.ELEMENTS.showListTrigger.classList.add(selectors.CLASSES.displayNone);

		this.bindEvents();
		this.setup();
	}

	static init(isWidget = false) {
		// eslint-disable-next-line
		new ClinicSearch(isWidget);
	}

	/**
	 * Bind `this` explicitly for event listeners.
	 */
	bindEvents() {
		[
			'onMediaQueryChange',
			// For Mobile
			'onShowFilters',
			'onHideFilters',
			'onSaveFilters',
			'onClearFilters',
			'onShowSort',
			'onHideSort',
			'_helperOnHideFilters',
			'_helperOnHideSort',
		].forEach((e) => {
			this[e] = this[e].bind(this);
		});
	}

	/**
	 * Is mobile media query met.
	 * @return {boolean}
	 */
	isMobile() {
		return this.mediaQuery.matches;
	}

	returnToInitStateFormItems() {
		selectors.ELEMENTS.formRadioList.forEach((item) => {
			// eslint-disable-next-line no-param-reassign
			item.checked = this.initState[item.value];
		});

		selectors.ELEMENTS.formCheckboxList.forEach((item) => {
			// eslint-disable-next-line no-param-reassign
			item.checked = this.initState[item.name];
		});
	}

	returnToInitStateSortFormItems() {
		selectors.ELEMENTS.sortFormRadioList.forEach((item) => {
			// eslint-disable-next-line no-param-reassign
			item.checked = this.initStateSort[item.value];
		});
	}

	onClearFilters() {
		this.onClearFilters = this.onClearFilters.bind(this);

		selectors.ELEMENTS.formFilters.reset();
	}

	onSaveFilters() {
		this.onHideFilters();
		selectors.ELEMENTS.formFilters.submit();
	}

	_helperOnHideFilters() {
		this.returnToInitStateFormItems();
		clinicFilter.helperOnHideFilters();
	}

	_helperOnHideSort() {
		this.returnToInitStateSortFormItems();
		clinicSort.helperOnHideSort();
	}

	onHideFilters() {
		// Hide filter screen
		clinicFilter.onHideFilters();

		selectors.ELEMENTS.hideFiltersContainer.addEventListener(
			'transitionend',
			this._helperOnHideFilters
		);
	}

	onShowFilters() {
		selectors.ELEMENTS.hideFiltersContainer.removeEventListener(
			'transitionend',
			this._helperOnHideFilters
		);
		// After removing event listener run onShowFilters from ./ClinicFilters which displays filter screen
		clinicFilter.onShowFilters();
	}

	onHideSort() {
		// Hide filter sort-filter screen
		clinicSort.onHideSort();

		selectors.ELEMENTS.hideSortContainer.addEventListener('transitionend', this._helperOnHideSort);
	}

	onShowSort() {
		selectors.ELEMENTS.hideSortContainer.removeEventListener(
			'transitionend',
			this._helperOnHideSort
		);
		// After removing event listener run onShowSort from ./ClinicSort which displays sort-filter screen
		clinicSort.onShowSort();
	}

	/**
	 * When the media query changes to desktop remove all mobile classes.
	 * @param {MediaQueryListEvent} e
	 */
	onMediaQueryChange(e) {
		if (!e.matches) {
			this.destroy();
		} else {
			this.setupEventListeners();
		}
	}

	setupEventListeners() {
		selectors.ELEMENTS.showMapTrigger.addEventListener('click', clinicMap.onShowMap);
		selectors.ELEMENTS.showListTrigger.addEventListener('click', clinicMap.onHideMap);
		// For mobile
		selectors.ELEMENTS.hideFilters.forEach((e) =>
			e.addEventListener('click', (event) => this.onHideFilters(event))
		);
		selectors.ELEMENTS.showFilters.addEventListener('click', this.onShowFilters);
		selectors.ELEMENTS.clearFilters.addEventListener('click', this.onClearFilters);
		selectors.ELEMENTS.saveFilters.addEventListener('click', this.onSaveFilters);

		selectors.ELEMENTS.hideSort.addEventListener('click', this.onHideSort);
		selectors.ELEMENTS.showSort.addEventListener('click', this.onShowSort);
	}

	setup() {
		this.mediaQuery.addEventListener('change', this.onMediaQueryChange);

		this.setupEventListeners();
	}

	destroy() {
		selectors.ELEMENTS.hideFilters.forEach((e) =>
			e.removeEventListener('click', (event) => this.onHideFilters(event))
		);
		selectors.ELEMENTS.showFilters.removeEventListener('click', this.onShowFilters);
		selectors.ELEMENTS.clearFilters.removeEventListener('click', this.onClearFilters);
		selectors.ELEMENTS.saveFilters.removeEventListener('click', this.onSaveFilters);
		selectors.ELEMENTS.hideFiltersContainer.removeEventListener(
			'transitionend',
			this._helperOnHideFilters
		);

		selectors.ELEMENTS.hideSort.removeEventListener('click', this.onHideSort);
		selectors.ELEMENTS.showSort.removeEventListener('click', this.onShowSort);
		selectors.ELEMENTS.hideSortContainer.removeEventListener(
			'transitionend',
			this._helperOnHideSort
		);
	}
}
